/**
 * The sticky offline banner (`docs/OFFLINE.md` §2): shown under the top bar
 * whenever the app is offline or a write is still queued, with a link to
 * the rejects sheet once the server has finally refused one.
 */
import { useState } from 'react';

import { Button, Sheet } from '../components/ui';
import { t } from '../i18n/fr';
import { remove } from './queue';
import { useOfflineStatus } from './useOfflineStatus';
import './offline.css';

import type { QueueEntry } from './queue';

/** What kind of write a queue entry is, guessed from its path, for the sheet's label. */
function entryKind(entry: QueueEntry): 'visit' | 'registration' | 'engagement' | 'other' {
  if (/\/visit\/$/.test(entry.path)) return 'visit';
  if (/registration-case\/$/.test(entry.path)) return 'registration';
  if (/^engagements\//.test(entry.path)) return 'engagement';
  return 'other';
}

/** The French label of an entry's kind, for the sheet. */
function kindLabel(kind: ReturnType<typeof entryKind>): string {
  switch (kind) {
    case 'visit':
      return t('offline.sheet.kind.visit');
    case 'registration':
      return t('offline.sheet.kind.registration');
    case 'engagement':
      return t('offline.sheet.kind.engagement');
    default:
      return t('offline.sheet.kind.other');
  }
}

/** The French reason for a rejection, from the server's `code` (`docs/OFFLINE.md` §3). */
function reasonLabel(code: string | undefined): string {
  switch (code) {
    case 'door_already_recorded':
      return t('offline.rejected.doorAlreadyRecorded');
    case 'door_held_by_other':
      return t('offline.rejected.doorHeldByOther');
    case 'invalid_visit':
      return t('offline.rejected.invalidVisit');
    case 'consent_required':
      return t('offline.rejected.consentRequired');
    default:
      return t('offline.rejected.other');
  }
}

/** The visit's local note, so the volunteer can copy it elsewhere — `undefined` for any other kind. */
function noteOf(entry: QueueEntry): string | undefined {
  if (entryKind(entry) !== 'visit') return undefined;
  const note = entry.body.note;
  return typeof note === 'string' && note.trim() !== '' ? note : undefined;
}

/** One rejected entry, in the sheet. */
function RejectedItem({ entry }: { entry: QueueEntry }) {
  const note = noteOf(entry);
  return (
    <div className="offline-rejects__item">
      <p className="offline-rejects__kind">{kindLabel(entryKind(entry))}</p>
      <p className="offline-rejects__reason">{reasonLabel(entry.lastError?.code)}</p>
      {note ? (
        <p className="offline-rejects__note">
          <span className="offline-rejects__note-label">{t('offline.sheet.noteLabel')}</span>
          {note}
        </p>
      ) : null}
      <Button variant="secondary" onClick={() => void remove(entry.id)}>
        {t('offline.sheet.remove')}
      </Button>
    </div>
  );
}

/**
 * Render the offline banner, plus the rejects sheet it opens. Rendered once,
 * inside `AppShell`, under the top bar.
 *
 * @returns The banner element, or `null` when there is nothing to say.
 */
export function OfflineBanner() {
  const { online, pending, rejected } = useOfflineStatus();
  const [sheetOpen, setSheetOpen] = useState(false);

  const visible = !online || pending > 0 || rejected.length > 0;
  if (!visible) {
    return null;
  }

  const message = !online
    ? pending > 0
      ? t('offline.banner.offlineWithPending', { count: pending, plural: pending > 1 ? 's' : '' })
      : t('offline.banner.offline')
    : pending > 0
      ? t('offline.banner.sending', { count: pending, plural: pending > 1 ? 's' : '' })
      : t('offline.banner.rejectedOnly', {
          count: rejected.length,
          plural: rejected.length > 1 ? 's' : '',
        });

  return (
    <div className="offline-banner" role="status">
      <span className="offline-banner__message">{message}</span>
      {rejected.length > 0 ? (
        <button type="button" className="offline-banner__link" onClick={() => setSheetOpen(true)}>
          {t('offline.banner.reviewLink', { count: rejected.length })}
        </button>
      ) : null}
      <Sheet open={sheetOpen} onClose={() => setSheetOpen(false)} title={t('offline.sheet.title')}>
        <div className="offline-rejects">
          {rejected.length === 0 ? (
            <p className="offline-rejects__empty">{t('offline.sheet.empty')}</p>
          ) : (
            rejected.map((entry) => <RejectedItem key={entry.id} entry={entry} />)
          )}
        </div>
      </Sheet>
    </div>
  );
}
